/**
 * Listado de leads de la cohorte (auditoría de cifras).
 * @module-isolated analytics-cohort
 */
import { useMemo, useState } from 'react';
import { ANALYTICS_CONFIG } from '../../../config/analytics-config.js';
import { SimpleTable } from '../../../shared/components/index.js';
import { meetingStatusLabel } from '../../../shared/constants/meeting-statuses.js';
import { isMetaLead } from '../utils/filter-cohort.js';

function formatCreated(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
}

function originLabel(lead) {
  if (isMetaLead(lead)) return 'Meta';
  if (lead.originStatus === 'none') return 'Sin etiqueta';
  if (lead.originStatus === 'unclassified') return 'Sin clasificar';
  return lead.originTag || '—';
}

export default function CohortLeadTable({ leads, stateName, meetingFilterLabel }) {
  const [search, setSearch] = useState('');
  const rows = useMemo(() => {
    return (leads || [])
      .slice()
      .sort(function (a, b) {
        return String(b.createdAtIso || '').localeCompare(String(a.createdAtIso || ''));
      })
      .map((lead) => {
      const id = String(lead.leadId || '');
      const href = ANALYTICS_CONFIG.kommoLeadUrlTemplate.replace('{id}', encodeURIComponent(id));
      return {
        id: id,
        name: lead.name || '—',
        kommoHref: href,
        leadId: id,
        createdAt: formatCreated(lead.createdAtIso),
        pipeline: lead.pipelineName || lead.pipelineId || '—',
        stage: lead.stageName || lead.stageId || '—',
        seller: lead.sellerId || '—',
        origin: originLabel(lead),
        campaign: isMetaLead(lead)
          ? lead.metaCampaignName || lead.metaCampaignId || '—'
          : '—',
        campaignId: isMetaLead(lead) ? lead.metaCampaignId || '—' : '—',
        meetingStatus: meetingStatusLabel(lead.meetingStatus) || '—'
      };
    });
  }, [leads]);

  const filtered = useMemo(() => {
    const q = String(search || '')
      .trim()
      .toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        r.name,
        r.leadId,
        r.pipeline,
        r.stage,
        r.seller,
        r.origin,
        r.campaign,
        r.campaignId,
        r.meetingStatus
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  const columns = [
    { key: 'name', label: 'Lead' },
    { key: 'leadId', label: 'Kommo ID' },
    { key: 'createdAt', label: 'Creado at' },
    { key: 'pipeline', label: 'Pipeline' },
    { key: 'stage', label: 'Estado Kommo' },
    { key: 'meetingStatus', label: 'Reunión' },
    { key: 'seller', label: 'Responsable' },
    { key: 'origin', label: 'Origen' },
    { key: 'campaign', label: 'Campaña Meta' },
    { key: 'campaignId', label: 'Campaign ID' }
  ];

  return (
    <section className="analytics-cohort-table" aria-label="Detalle de leads">
      <div className="analytics-cohort-states-head">
        <h2 className="analytics-cohort-h2">
          {[
            stateName || (meetingFilterLabel ? '' : 'Detalle'),
            meetingFilterLabel || '',
            String(filtered.length)
          ]
            .filter(Boolean)
            .join(' · ')}
        </h2>
        <p className="analytics-cohort-note">
          {meetingFilterLabel
            ? 'Solo leads cuya etapa Kommo actual está mapeada a ' +
              meetingFilterLabel.toLowerCase() +
              '.'
            : stateName
              ? 'Leads de este estado hoy. Clic en el estado de nuevo para ver todos.'
              : 'Todos los leads del universo filtrado.'}
        </p>
      </div>
      <SimpleTable
        columns={columns}
        rows={filtered}
        search={search}
        onSearchChange={setSearch}
        emptyText="Ningún lead coincide."
      />
    </section>
  );
}
